import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useJournal } from '../hooks/useJournal';
import { useBadges } from '../hooks/useBadges';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useLivros } from '../hooks/useLivros';
import { useDesafios } from '../hooks/useDesafios';
import BadgeShelf from '../components/gamification/BadgeShelf';
import ReflectionHistory from '../components/journal/ReflectionHistory';
import MoodChart from '../components/profile/MoodChart'
import MonthCalendar from '../components/profile/MonthCalendar';
import ModalFeedback from '../components/feedback/ModalFeedback';
import ShareDesafioModal from '../components/desafios/ShareDesafioModal';
import { ALL_BADGES } from '../lib/badgeEngine';

// SVG icons
const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const LogOutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const BellIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const FlameIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2c0 0-4 4-4 8.5a4 4 0 0 0 8 0C16 6 12 2 12 2z" />
    <path d="M12 12c0 0-2 1.5-2 3.5a2 2 0 0 0 4 0C14 13.5 12 12 12 12z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

// Avatar baseado nas iniciais do nome
function InitialAvatar({ name, size = 76 }) {
  const initials = name
    ? name.trim().split(' ').slice(0, 2).map(w => w[0].toUpperCase()).join('')
    : '?';

  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #2f2b5f, #7b5ea7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: '#f4f2ee',
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: size * 0.38,
      fontWeight: 400,
      letterSpacing: '-0.01em',
      userSelect: 'none',
    }}>
      {initials}
    </div>
  );
}

const EMOJIS_AVATAR = [
  '👩🏻', '👩🏼', '👩🏽', '👩‍🦱', '👩🏽‍🦱', '👱‍♀️', '👩‍🦰', '👩‍🦳', '🧕🏽', '👩🏾',
  '👨', '👨🏻', '👨🏽', '👨‍🦱',
  '🧘‍♀️', '🧘🏾‍♀️', '🧘‍♂️', '🏊‍♀️', '🏃‍♀️', '🚴‍♀️',
  '🌿', '🌸', '🌺', '🌙', '🌱', '🦋', '🌊', '☀️',
];

export default function ProfilePage({ user, profile, needsSetup, onSave, onLogout, onVerPolitica, onExcluirConta }) {
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [avatarEmoji, setAvatarEmoji] = useState(profile?.avatar_emoji || '');
  const [mostraEmojiPicker, setMostraEmojiPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [modalFeedbackVisivel, setModalFeedbackVisivel] = useState(false);
  const [desafioParaCompartilhar, setDesafioParaCompartilhar] = useState(null);

  // Exclusão de conta
  const [deleteStep, setDeleteStep] = useState(0); // 0=oculto 1=confirmação 2=digitando 3=deletando
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const handleExcluirConta = useCallback(async () => {
    if (deleteInput !== 'EXCLUIR') {
      setDeleteError('Digite EXCLUIR (em maiúsculas) para confirmar.');
      return;
    }
    setDeleteStep(3);
    setDeleteError('');
    try {
      const { error: rpcError } = await supabase.rpc('delete_my_account');
      if (rpcError) throw rpcError;
      // Desloga após exclusão bem-sucedida
      await onLogout();
    } catch (e) {
      console.error('[delete_my_account] erro:', e);
      setDeleteError('Ocorreu um erro. Tente novamente ou entre em contato: contato@oclubedoeu.com.br');
      setDeleteStep(2);
    }
  }, [deleteInput, onLogout]);

  const { logs, streak, fetchData } = useJournal(user.id);
  const { earnedBadges, fetchBadges } = useBadges(user.id);
  const { supported, permission, isSubscribed, loading: pushLoading, subscribe, unsubscribe } = usePushNotifications(user.id);
  const { stats: livrosStats, fetchLivros } = useLivros(user.id);
  const { desafioAtivo, desafiosConcluidos, fetchDesafios } = useDesafios(user.id);

  useEffect(() => {
    fetchData(user.id);
    fetchBadges();
    fetchLivros();
    fetchDesafios();
  }, [user.id]); // eslint-disable-line

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || '');
      setAvatarEmoji(profile.avatar_emoji || '');
    }
  }, [profile]);

  const handleSave = async () => {
    if (!displayName.trim()) { setError('Digite um nome'); return; }
    setError('');
    setSaving(true);
    const emoji = avatarEmoji || displayName[0]?.toUpperCase() || '?';
    const err = await onSave({ display_name: displayName, avatar_emoji: emoji });
    setSaving(false);
    if (err) {
      setError(err.message);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  const totalEntries = logs.filter(l => l.completed).length;
  const thisMonth = logs.filter(l => {
    const d = new Date(l.date);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() && l.completed;
  }).length;
  const reflexoesEscritas = logs.filter(l => l.note && l.note.trim().length > 0).length;
  const desafiosParticipados = desafiosConcluidos.length + (desafioAtivo ? 1 : 0);

  return (
    <div className="profile-page">

      {/* Cabeçalho */}
      <div className="section" style={{ marginTop: 28, marginBottom: 20 }}>
        <h1 style={{ marginBottom: 4, color: 'var(--navy)' }}>
          {needsSetup ? 'Configure seu perfil' : 'Seu perfil'}
        </h1>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>
          {user.email}
        </p>
      </div>

      {/* Card de perfil */}
      <div className="section">
        <div className="card">
          <div className="profile-avatar-row">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              {avatarEmoji ? (
                <div style={{
                  width: 76, height: 76, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #2f2b5f, #7b5ea7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 36,
                }}>
                  {avatarEmoji}
                </div>
              ) : (
                <InitialAvatar name={displayName || user.email} />
              )}
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: 'var(--lavender)', padding: 0 }}
                onClick={() => setMostraEmojiPicker(v => !v)}
              >
                {mostraEmojiPicker ? 'Fechar' : 'Mudar avatar'}
              </button>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)', marginBottom: 8 }}>
                Nome de exibição
              </p>
              <input
                className="input"
                placeholder="Como quer ser chamado?"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                maxLength={40}
              />
            </div>
          </div>

          {mostraEmojiPicker && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 0 4px' }}>
              {EMOJIS_AVATAR.map(e => (
                <button
                  key={e}
                  onClick={() => { setAvatarEmoji(e); setMostraEmojiPicker(false); }}
                  style={{
                    fontSize: 28, background: avatarEmoji === e ? 'var(--lavender-light, #f0eef8)' : 'transparent',
                    border: avatarEmoji === e ? '2px solid var(--lavender)' : '2px solid transparent',
                    borderRadius: 10, width: 48, height: 48, cursor: 'pointer', lineHeight: 1,
                  }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            {saved && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: 'var(--teal)' }}>
                <CheckIcon /> Salvo!
              </span>
            )}
            {error && (
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: 'var(--danger)' }}>
                {error}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Ajude a melhorar */}
      <div className="section">
        <div className="perfil-feedback-card">
          <span className="perfil-feedback-card-emoji">💬</span>
          <p className="perfil-feedback-card-titulo">Sua voz importa</p>
          <p className="perfil-feedback-card-descricao">
            Sugira funcionalidades, desafios, conteúdos, reporte bugs ou mande um elogio.
          </p>
          <button
            type="button"
            className="perfil-feedback-card-btn"
            onClick={() => setModalFeedbackVisivel(true)}
          >
            Enviar mensagem
          </button>
        </div>
      </div>

      <ModalFeedback
        visivel={modalFeedbackVisivel}
        onFechar={() => setModalFeedbackVisivel(false)}
      />

      {/* Estatísticas */}
      <div className="section">
        <p className="section-title">Suas estatísticas</p>
        <div className="profile-stats">
          <div className="stat-card">
            <div className="stat-value">{streak}</div>
            <div className="stat-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <FlameIcon /> dias consecutivos
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{thisMonth}</div>
            <div className="stat-label">Este mês</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{totalEntries}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{livrosStats.livrosConcluidos}</div>
            <div className="stat-label">Livros lidos</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{reflexoesEscritas}</div>
            <div className="stat-label">Reflexões escritas</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{desafiosParticipados}</div>
            <div className="stat-label">Desafios participados</div>
          </div>
        </div>
      </div>

      {/* Calendário de presença */}
      <div className="section">
        <p className="section-title">Minha presença</p>
        <MonthCalendar userId={user.id} logs={logs} streak={streak} />
      </div>

      {/* Gráfico de humor */}
      <div className="section">
        <p className="section-title">Humor — últimos 30 dias</p>
        <div className="card">
          <MoodChart logs={logs} days={30} />
        </div>
      </div>

      {/* Conquistas */}
      <div className="section">
        <p className="section-title">Conquistas</p>
        <BadgeShelf badges={earnedBadges} />
      </div>

      {/* Notificações — sempre visível */}
      <div className="section">
        <p className="section-title">Lembrete diário</p>
        <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ color: 'var(--navy)', opacity: 0.5, marginTop: 2, flexShrink: 0 }}>
              <BellIcon />
            </div>
            <div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, color: 'var(--navy)', marginBottom: 3 }}>
                Notificações
              </p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 300, color: 'var(--muted)', lineHeight: 1.5 }}>
                {isSubscribed
                  ? 'Você receberá um lembrete às 20h caso não tenha feito check-in.'
                  : permission === 'denied'
                  ? 'Notificações bloqueadas. Reative nas configurações do seu navegador.'
                  : 'Receba um lembrete para não esquecer seu check-in diário.'}
              </p>
            </div>
          </div>
          <button
            onClick={
              permission === 'denied' ? undefined :
              supported ? (isSubscribed ? unsubscribe : subscribe) :
              () => alert('Para ativar notificações no iPhone, adicione o app à tela inicial: toque em Compartilhar (□↑) → "Adicionar à Tela Inicial". Depois abra pelo ícone e ative aqui.')
            }
            disabled={pushLoading || permission === 'denied'}
            className={`btn btn-sm ${isSubscribed ? 'btn-ghost' : permission === 'denied' ? 'btn-ghost' : 'btn-primary'}`}
            title={permission === 'denied' ? 'Reative nas configurações do seu navegador' : undefined}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {pushLoading ? '...' : permission === 'denied' ? 'Bloqueado' : isSubscribed ? 'Desativar' : 'Ativar'}
          </button>
        </div>
      </div>

      {/* Desafios concluídos */}
      {desafiosConcluidos.length > 0 && (
        <div className="section">
          <p className="section-title">Desafios concluídos</p>
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {desafiosConcluidos.map((d, i) => {
              const conquistaBadge = d.desafio_id?.toLowerCase().includes('gratidao') &&
                earnedBadges.find(b => b.slug === 'desafio_gratidao' && b.earned)
                  ? ALL_BADGES.find(b => b.slug === 'desafio_gratidao')
                  : null;
              return (
                <div key={d.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, color: 'var(--navy)', marginBottom: 2 }}>
                      {d.desafio_nome}
                    </p>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: 'var(--muted)' }}>
                      {d.duracao} dias
                      {d.data_conclusao ? ` · ${new Date(d.data_conclusao).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                    </p>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, fontSize: 13 }}
                    onClick={() => setDesafioParaCompartilhar({ desafio: d, conquista: conquistaBadge })}
                  >
                    <ShareIcon /> Compartilhar
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal de compartilhar conquista */}
      {desafioParaCompartilhar && (
        <ShareDesafioModal
          desafio={desafioParaCompartilhar.desafio}
          conquista={desafioParaCompartilhar.conquista}
          onFechar={() => setDesafioParaCompartilhar(null)}
        />
      )}

      {/* Histórico */}
      <div className="section">
        <p className="section-title">Histórico de reflexões</p>
        <ReflectionHistory logs={logs} />
      </div>

      {/* Privacidade e dados */}
      <div className="section">
        <p className="section-title">Privacidade e dados</p>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Link política */}
          <button
            type="button"
            className="perfil-privacidade-item"
            onClick={onVerPolitica}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--navy)' }}>
              <ShieldIcon />
              <span>Política de Privacidade</span>
            </span>
            <span style={{ color: 'var(--muted)', fontSize: 18, lineHeight: 1 }}>›</span>
          </button>

          <div className="perfil-divisor" />

          {/* Excluir conta */}
          {deleteStep === 0 && (
            <button
              type="button"
              className="perfil-privacidade-item"
              onClick={() => setDeleteStep(1)}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)' }}>
                <TrashIcon />
                <span>Excluir minha conta</span>
              </span>
              <span style={{ color: 'var(--muted)', fontSize: 18, lineHeight: 1 }}>›</span>
            </button>
          )}

          {deleteStep === 1 && (
            <div className="delete-confirm-box">
              <p className="delete-confirm-titulo">Tem certeza?</p>
              <p className="delete-confirm-texto">
                Isso apaga <strong>permanentemente</strong> todos os seus dados — humor, reflexões,
                streak, conquistas. Não tem como desfazer.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setDeleteStep(0)}>
                  Cancelar
                </button>
                <button className="btn btn-sm delete-btn-confirmar" onClick={() => setDeleteStep(2)}>
                  Continuar
                </button>
              </div>
            </div>
          )}

          {(deleteStep === 2 || deleteStep === 3) && (
            <div className="delete-confirm-box">
              <p className="delete-confirm-titulo">Confirmação final</p>
              <p className="delete-confirm-texto">
                Digite <strong>EXCLUIR</strong> para confirmar a exclusão permanente da sua conta.
              </p>
              <input
                className="input"
                placeholder="EXCLUIR"
                value={deleteInput}
                onChange={e => setDeleteInput(e.target.value)}
                disabled={deleteStep === 3}
                style={{ marginBottom: 10 }}
              />
              {deleteError && (
                <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 8 }}>{deleteError}</p>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => { setDeleteStep(0); setDeleteInput(''); setDeleteError(''); }} disabled={deleteStep === 3}>
                  Cancelar
                </button>
                <button className="btn btn-sm delete-btn-confirmar" onClick={handleExcluirConta} disabled={deleteStep === 3}>
                  {deleteStep === 3 ? 'Excluindo...' : 'Excluir conta'}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Logout */}
      <div className="section" style={{ paddingBottom: 12 }}>
        <button
          onClick={onLogout}
          className="btn btn-ghost"
          style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted)' }}
        >
          <LogOutIcon />
          Sair da conta
        </button>
      </div>

    </div>
  );
}
